import { Typography } from '@components'
import { productChannel, useEventBusOn } from '@haus-storefront-react/core'
import { useProduct } from '@haus-storefront-react/hooks'
import type { Product, ProductVariant } from '@haus-storefront-react/shared-types'
import { getVendureProductId } from '../../utilities/utilities'
import type { ProductVariantDataAttributes } from './_propsFn'

const CUSTOM_FIELD_PREFIX = 'custom:'

const ProductVariantDataWidget = ({
  selectedField,
  facetCode,
  configStyles,
}: ProductVariantDataAttributes) => {
  const [selectedProductVariant] = useEventBusOn(
    productChannel,
    'product:variant:selected',
    undefined,
    undefined,
    false, // Do not replay past events when subscribing; only respond to new selections
  )

  const vendureProductId = getVendureProductId()
  const { data: product } = useProduct({ id: vendureProductId }, !!vendureProductId)

  if (!selectedProductVariant) {
    return null
  }

  let value: string | null = null
  try {
    value = getSelectedFieldValue(selectedField, selectedProductVariant, product ?? null, facetCode)
  } catch {
    return null
  }

  if (value === null) {
    return null
  }

  return (
    <Typography
      variant={configStyles?.variantDataTypography?.variant ?? 'body'}
      as={configStyles?.variantDataTypography?.tag ?? 'span'}
      color={configStyles?.variantDataTypography?.color ?? 'body'}
    >
      {value}
    </Typography>
  )
}

/** Convert any scalar value to a display string; non-renderable values (objects, images) return null. */
function toDisplayString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return null
}

/** Resolve the raw field value from the variant or product. Facet values are pre-joined as a comma-separated string. */
function getFieldValue(
  selectedField: string,
  variant: ProductVariant,
  product: Product | null,
  facetCode?: string,
): unknown {
  if (selectedField === 'sku') {
    return variant.sku
  }

  if (selectedField === 'name') {
    return variant.name
  }

  if (selectedField === 'facet' && facetCode && product?.facetValues) {
    const labels: string[] = []
    for (const fv of product.facetValues) {
      if (fv.facet?.code !== facetCode) {
        continue
      }
      const label = toDisplayString(fv.name) ?? toDisplayString(fv.code)
      if (label !== null) {
        labels.push(label)
      }
    }
    return labels.length > 0 ? labels.join(', ') : null
  }

  if (selectedField.startsWith(CUSTOM_FIELD_PREFIX)) {
    const name = selectedField.slice(CUSTOM_FIELD_PREFIX.length).trim()
    return name ? ((variant.customFields as Record<string, unknown>)?.[name] ?? null) : null
  }

  return null
}

/** Resolve and convert a field value to a display string, returning null for non-renderable values. */
function getSelectedFieldValue(
  selectedField: string,
  variant: ProductVariant,
  product: Product | null,
  facetCode?: string,
): string | null {
  const fieldValue = getFieldValue(selectedField, variant, product, facetCode)
  return toDisplayString(fieldValue)
}

export default ProductVariantDataWidget
